// Erkennung, Formatierung und Einfaerbung von Nachrichteninhalten (Payload, Header, Attachments).
//
// Bewusst ohne Fremdbibliothek: es geht um wenige Formate, und ein Highlighter im Bundle waere
// fuer diesen Zweck unverhaeltnismaessig. Die Einfaerbung nutzt die vorhandenen
// --iio-syntax-*-Tokens, damit sie in beiden Themes trägt.
//
// WICHTIG: Alle Ausgaben sind HTML-escaped, bevor Markup entsteht. Der Inhalt kommt vom Tenant
// und darf niemals als HTML interpretiert werden.

export const escapeHtml = (s) => String(s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

// ── Erkennung ────────────────────────────────────────────────────────────────────────
// Reihenfolge zaehlt: SOAP ist XML, JSON-Zeilen sind kein CSV, EDIFACT beginnt mit UNB/UNA.
export function erkenneTyp(text) {
  const t = String(text || "").trim();
  if (!t) return { id: "leer", label: "Leer" };
  if (/^<\?xml/i.test(t) || /^<[a-z_][\w.:-]*[\s>/]/i.test(t)) {
    if (/<(\w+:)?Envelope[\s>]/i.test(t)) return { id: "xml", label: "SOAP" };
    if (/^<!DOCTYPE html|^<html[\s>]/i.test(t)) return { id: "xml", label: "HTML" };
    return { id: "xml", label: "XML" };
  }
  if (/^[[{]/.test(t)) {
    try { JSON.parse(t); return { id: "json", label: "JSON" }; } catch { /* kein valides JSON */ }
    return { id: "text", label: "Text" };
  }
  if (/^(UNA|UNB)[:+]/.test(t)) return { id: "edifact", label: "EDIFACT" };
  if (/^E?DC\s|^EDI_DC40/.test(t)) return { id: "text", label: "IDoc (flach)" };
  // CSV nur, wenn mehrere Zeilen dieselbe Trennzeichenzahl haben - sonst ist es Freitext.
  const zeilen = t.split(/\r?\n/).filter(Boolean).slice(0, 8);
  if (zeilen.length >= 2) {
    for (const trenner of [";", ",", "\t", "|"]) {
      const n = zeilen[0].split(trenner).length;
      if (n >= 2 && zeilen.every(z => z.split(trenner).length === n)) {
        return { id: "csv", label: "CSV", trenner };
      }
    }
  }
  return { id: "text", label: "Text" };
}

// ── Formatierung ─────────────────────────────────────────────────────────────────────
export function formatiere(text, typ) {
  const t = String(text || "");
  try {
    if (typ.id === "json") return JSON.stringify(JSON.parse(t.trim()), null, 2);
    if (typ.id === "xml") return formatiereXml(t);
    if (typ.id === "edifact") return t.replace(/'(?!$)/g, "'\n"); // Segmentende ist der Apostroph
  } catch { /* Formatierung ist Komfort - schlaegt sie fehl, bleibt das Original */ }
  return t;
}

// Einrueckung ohne DOM-Parser: der Tenant liefert teils ungueltiges XML, DOMParser wuerde dann
// gar nichts liefern. Diese Variante kommt auch mit Bruchstuecken zurecht.
function formatiereXml(xml) {
  const kompakt = xml.replace(/>\s+</g, "><").trim();
  const teile = kompakt.replace(/></g, "> <").split(" ");
  let tiefe = 0;
  const zeilen = [];
  for (const teil of teile) {
    const schluss = /^<\//.test(teil);
    const selbst = /\/>$/.test(teil) || /^<\?/.test(teil) || /^<!/.test(teil);
    const oeffnend = /^<[^/!?]/.test(teil) && !selbst;
    if (schluss) tiefe = Math.max(0, tiefe - 1);
    zeilen.push("  ".repeat(tiefe) + teil);
    if (oeffnend) tiefe++;
  }
  return zeilen.join("\n");
}

// ── Einfaerbung ──────────────────────────────────────────────────────────────────────
// Gibt HTML mit <span class="tk-*"> zurueck. Eingabe wird vorher escaped.
export function faerbe(text, typ) {
  const roh = escapeHtml(text);
  if (typ.id === "xml") {
    return roh
      // Kommentare zuerst, damit ihr Inhalt nicht weiter zerlegt wird
      .replace(/&lt;!--[\s\S]*?--&gt;/g, m => `<span class="tk-com">${m}</span>`)
      .replace(/(&lt;\/?)([\w.:-]+)/g, (m, a, b) => `${a}<span class="tk-tag">${b}</span>`)
      .replace(/([\w.:-]+)=(&quot;[^&]*?&quot;)/g,
        (m, a, b) => `<span class="tk-attr">${a}</span>=<span class="tk-str">${b}</span>`);
  }
  if (typ.id === "json") {
    return roh
      .replace(/(&quot;(?:[^&\\]|\\.)*?&quot;)(\s*:)/g, (m, a, b) => `<span class="tk-key">${a}</span>${b}`)
      .replace(/:(\s*)(&quot;(?:[^&\\]|\\.)*?&quot;)/g, (m, a, b) => `:${a}<span class="tk-str">${b}</span>`)
      .replace(/\b(true|false|null)\b/g, m => `<span class="tk-kw">${m}</span>`)
      .replace(/(:\s*)(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g, (m, a, b) => `${a}<span class="tk-num">${b}</span>`);
  }
  if (typ.id === "edifact") {
    return roh.replace(/^([A-Z]{3})(?=[:+])/gm, m => `<span class="tk-tag">${m}</span>`);
  }
  if (typ.id === "csv" && typ.trenner) {
    const tr = escapeHtml(typ.trenner);
    const zeilen = roh.split("\n");
    return zeilen.map((z, i) => {
      const gefaerbt = z.split(tr).join(`<span class="tk-sep">${tr}</span>`);
      return i === 0 ? `<span class="tk-key">${gefaerbt}</span>` : gefaerbt;
    }).join("\n");
  }
  return roh;
}
